import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { add } from 'date-fns/add';
import { nextMonday } from 'date-fns/nextMonday';
import { set } from 'date-fns/set';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { DeliveryMethodToggles } from 'src/components/DeliveryMethodToggles';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { TextField } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { DeliveryMethod } from 'src/models/conversation/_types';
import { User } from 'src/models/user/_types';
import { isTrainer } from 'src/models/user/utils';
import { formatListDateAndTime } from 'src/utils/dateFormatter';

interface SendMessageButtonProps {
  user: User;
  sendingDisabled: boolean;
  onClickSendMessage: (options: {
    message: string;
    sendAt: Date | null;
    delivery?: DeliveryMethod[];
    expiresOn?: Date | null;
    subject?: string;
  }) => void;
  newMessage: string;
  sendAt: Date | null;
  setSendAt: (date: Date) => void;
  setModalOpen: (open: boolean) => void;
  defaultDelivery?: DeliveryMethod[];
  isAppAlert?: boolean;
  expiresOn?: Date | null;
  setExpirationModalOpen?: (open: boolean) => void;
}

export const SendMessageButton: React.FC<SendMessageButtonProps> = ({
  user,
  sendingDisabled,
  onClickSendMessage,
  newMessage,
  sendAt,
  setSendAt,
  setModalOpen,
  defaultDelivery = ['in-app', 'email'],
  isAppAlert,
  expiresOn,
  setExpirationModalOpen,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });
  const [delivery, setDelivery] = useState<DeliveryMethod[]>(defaultDelivery);
  const [isOpen, setIsOpen] = useState(false);
  const [subject, setSubject] = useState('');

  const handleSendMessage = () => {
    onClickSendMessage({
      message: newMessage,
      sendAt,
      delivery,
      subject,
    });
  };

  if (!isTrainer(user)) {
    return (
      <IconButton
        disabled={sendingDisabled}
        onClick={handleSendMessage}
        sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          borderRadius: 3,
          '@media (hover: hover)': {
            '&:hover': { backgroundColor: 'primary.main' },
          },
        }}
      >
        <Icon icon={icon({ name: 'paper-plane-top', style: 'regular' })} />
      </IconButton>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: '21px',
        bgcolor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <IconButton
        disabled={sendingDisabled}
        onClick={handleSendMessage}
        sx={{
          color: 'white',
          borderRadius: '50%',
        }}
      >
        <Icon
          size="small"
          icon={icon({ name: 'paper-plane-top', style: 'regular' })}
        />
      </IconButton>

      <Box sx={{ width: '1px', bgcolor: 'primary.dark', height: '22px' }}></Box>

      <DropdownMenu
        open={isOpen}
        setOpen={setIsOpen}
        closeOnContentClick={false}
        trigger={
          <Tooltip title={t('labels.messageOptions')}>
            <IconButton
              disabled={sendingDisabled}
              sx={{
                color: 'white',
                borderRadius: '50%',
              }}
            >
              <Icon
                size="small"
                icon={icon({ name: 'angle-down', style: 'regular' })}
              />
            </IconButton>
          </Tooltip>
        }
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {!isAppAlert && (
          <Box sx={{ p: 2 }}>
            <DeliveryMethodToggles value={delivery} onChange={setDelivery} />
            {delivery.includes('email') && (
              <TextField
                fullWidth
                size="small"
                label={t('labels.emailSubject')}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                sx={{ mt: 2 }}
              />
            )}
          </Box>
        )}
        <Divider />
        <Typography color="text.secondary" variant="body2" px={2} pt={2}>
          {t('labels.scheduleMessage')}
        </Typography>
        <ListItem
          onClick={() => {
            const tomorrow = add(new Date(), { days: 1 });
            const tomorrowAt9AM = set(tomorrow, {
              hours: 9,
              minutes: 0,
              seconds: 0,
            });
            setSendAt(tomorrowAt9AM);
            onClickSendMessage({
              message: newMessage,
              sendAt: tomorrowAt9AM,
              delivery,
              subject,
            });
            setIsOpen(false);
          }}
          clickable={true}
          primary={t('messages.scheduledForTomorrow')}
        />
        <ListItem
          onClick={() => {
            const nextMondayAt9AM = set(nextMonday(new Date()), {
              hours: 9,
              minutes: 0,
              seconds: 0,
            });
            setSendAt(nextMondayAt9AM);
            onClickSendMessage({
              message: newMessage,
              sendAt: nextMondayAt9AM,
              delivery,
              subject,
            });
            setIsOpen(false);
          }}
          clickable={true}
          primary={t('messages.scheduledForMonday')}
        />
        <Divider />
        <ListItem
          onClick={() => {
            setSendAt(new Date());
            setModalOpen(true);
            setIsOpen(false);
          }}
          clickable={true}
          primary={t('buttons.customTime')}
        />
        {isAppAlert && (
          <>
            <Divider />
            <Typography color="text.secondary" variant="body2" px={2} pt={2}>
              {t('labels.expiration')}
            </Typography>
            {expiresOn ? (
              <ListItem
                primary={`${t('messages.expires')} ${formatListDateAndTime(
                  expiresOn
                )}`}
                secondary={t('labels.clickToChange')}
                onClick={() => setExpirationModalOpen?.(true)}
                clickable={true}
              />
            ) : (
              <ListItem
                primary={t('labels.setExpirationDate')}
                onClick={() => setExpirationModalOpen?.(true)}
                clickable={true}
              />
            )}
          </>
        )}
      </DropdownMenu>
    </Box>
  );
};
